import { useState, useRef, useCallback, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, Volume2, Clock, Headphones, Waves } from "lucide-react";

const AMBIENCE_TYPES = [
  { value: "none", label: "None" },
  { value: "ocean", label: "Ocean Waves" },
  { value: "rain", label: "Gentle Rain" },
  { value: "forest", label: "Forest Ambience" },
  { value: "white", label: "White Noise" },
];

const DURATIONS = [5, 10, 15, 20, 30];

export default function SoundPlayer({ condition, compact = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(30);
  const [duration, setDuration] = useState(10);
  const [ambience, setAmbience] = useState("none");
  const [ambienceVolume, setAmbienceVolume] = useState(15);
  const [solfeggio, setSolfeggio] = useState(true);
  const [fadeEffects, setFadeEffects] = useState(true);
  const [timeLeft, setTimeLeft] = useState(600);
  const [totalSeconds, setTotalSeconds] = useState(0);

  const audioCtxRef = useRef(null);
  const nodesRef = useRef({});
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);

  const freq = condition?.frequency || 10;
  const baseFreq = condition?.base_frequency || 200;
  const solfeggioFreq = condition?.solfeggio || 528;
  const brainwaveType = condition?.brainwave_type || "Alpha";

  const createNoiseBuffer = useCallback((ctx, type) => {
    const sr = ctx.sampleRate;
    const len = sr * 4;
    const buf = ctx.createBuffer(2, len, sr);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < len; i++) {
        const white = Math.random() * 2 - 1;
        if (type === "white") {
          data[i] = white * 0.3;
        } else {
          b0 = 0.99886 * b0 + white * 0.0555179; b1 = 0.99332 * b1 + white * 0.0750759;
          b2 = 0.96900 * b2 + white * 0.1538520; b3 = 0.86650 * b3 + white * 0.3104856;
          b4 = 0.55000 * b4 + white * 0.5329522; b5 = -0.7616 * b5 - white * 0.0168980;
          data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.05;
          b6 = white * 0.115926;
        }
      }
    }
    return buf;
  }, []);

  const startAudio = useCallback(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;

    const masterGain = ctx.createGain();
    const vol = volume / 100;
    masterGain.gain.setValueAtTime(fadeEffects ? 0.001 : vol, ctx.currentTime);
    if (fadeEffects) masterGain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 3);
    masterGain.connect(ctx.destination);

    // Binaural beats - left channel
    const leftOsc = ctx.createOscillator();
    const leftPan = ctx.createStereoPanner();
    leftOsc.type = "sine";
    leftOsc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
    leftPan.pan.setValueAtTime(-1, ctx.currentTime);
    leftOsc.connect(leftPan).connect(masterGain);
    leftOsc.start();

    // Binaural beats - right channel
    const rightOsc = ctx.createOscillator();
    const rightPan = ctx.createStereoPanner();
    rightOsc.type = "sine";
    rightOsc.frequency.setValueAtTime(baseFreq + freq, ctx.currentTime);
    rightPan.pan.setValueAtTime(1, ctx.currentTime);
    rightOsc.connect(rightPan).connect(masterGain);
    rightOsc.start();

    nodesRef.current = { masterGain, leftOsc, rightOsc };

    // Solfeggio overlay
    if (solfeggio) {
      const solOsc = ctx.createOscillator();
      const solGain = ctx.createGain();
      solOsc.type = "sine";
      solOsc.frequency.setValueAtTime(solfeggioFreq, ctx.currentTime);
      solGain.gain.setValueAtTime(0.08, ctx.currentTime);
      solOsc.connect(solGain).connect(masterGain);
      solOsc.start();
      nodesRef.current.solOsc = solOsc;
      nodesRef.current.solGain = solGain;
    }

    // Ambient noise
    if (ambience !== "none") {
      const noiseType = ambience === "white" ? "white" : "pink";
      const noiseBuf = createNoiseBuffer(ctx, noiseType);
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = noiseBuf;
      noiseSrc.loop = true;

      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(ambienceVolume / 100 * 0.5, ctx.currentTime);

      const filter = ctx.createBiquadFilter();
      if (ambience === "ocean") {
        filter.type = "lowpass"; filter.frequency.setValueAtTime(400, ctx.currentTime);
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.frequency.setValueAtTime(0.08, ctx.currentTime);
        lfoGain.gain.setValueAtTime(200, ctx.currentTime);
        lfo.connect(lfoGain).connect(filter.frequency);
        lfo.start();
        nodesRef.current.lfo = lfo;
      } else if (ambience === "rain") {
        filter.type = "bandpass"; filter.frequency.setValueAtTime(2000, ctx.currentTime); filter.Q.setValueAtTime(0.5, ctx.currentTime);
      } else if (ambience === "forest") {
        filter.type = "lowpass"; filter.frequency.setValueAtTime(800, ctx.currentTime);
      } else {
        filter.type = "allpass";
      }

      noiseSrc.connect(filter).connect(noiseGain).connect(masterGain);
      noiseSrc.start();
      nodesRef.current.noiseSrc = noiseSrc;
      nodesRef.current.noiseGain = noiseGain;
    }
  }, [volume, freq, baseFreq, solfeggioFreq, solfeggio, ambience, ambienceVolume, fadeEffects, createNoiseBuffer]);

  const stopAudio = useCallback(() => {
    const ctx = audioCtxRef.current;
    const nodes = nodesRef.current;
    if (!ctx || !nodes.masterGain) return;

    if (fadeEffects) {
      nodes.masterGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2);
      setTimeout(() => {
        try {
          nodes.leftOsc?.stop(); nodes.rightOsc?.stop();
          nodes.solOsc?.stop(); nodes.noiseSrc?.stop();
          nodes.lfo?.stop(); ctx.close();
        } catch (e) { /* already stopped */ }
      }, 2200);
    } else {
      try {
        nodes.leftOsc?.stop(); nodes.rightOsc?.stop();
        nodes.solOsc?.stop(); nodes.noiseSrc?.stop();
        nodes.lfo?.stop(); ctx.close();
      } catch (e) { /* already stopped */ }
    }
    audioCtxRef.current = null;
    nodesRef.current = {};
  }, [fadeEffects]);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      stopAudio();
      clearInterval(timerRef.current);
      const sessionMins = Math.round(totalSeconds / 60);
      if (sessionMins > 0) {
        const stats = JSON.parse(localStorage.getItem("th_stats") || '{"sessions":0,"minutes":0,"streak":0,"lastDate":""}');
        stats.sessions += 1;
        stats.minutes += sessionMins;
        const today = new Date().toDateString();
        if (stats.lastDate !== today) {
          const yesterday = new Date(Date.now() - 86400000).toDateString();
          stats.streak = stats.lastDate === yesterday ? stats.streak + 1 : 1;
          stats.lastDate = today;
        }
        localStorage.setItem("th_stats", JSON.stringify(stats));
      }
      setTotalSeconds(0);
    } else {
      startAudio();
      setTimeLeft(duration * 60);
      setTotalSeconds(0);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            stopAudio();
            clearInterval(timerRef.current);
            setIsPlaying(false);
            return 0;
          }
          return prev - 1;
        });
        setTotalSeconds(prev => prev + 1);
      }, 1000);
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, startAudio, stopAudio, duration, totalSeconds]);

  useEffect(() => {
    return () => { stopAudio(); clearInterval(timerRef.current); cancelAnimationFrame(animFrameRef.current); };
  }, [stopAudio]);

  // Waveform visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width = canvas.offsetWidth * 2;
    const h = canvas.height = canvas.offsetHeight * 2;
    let phase = 0;
    const colors = { Delta: '#7C3AED', Theta: '#0D9488', Alpha: '#B8860B', Beta: '#DC6B3F' };
    const color = colors[brainwaveType] || '#7C3AED';

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      if (isPlaying) {
        phase += 0.03;
        for (let wave = 0; wave < 3; wave++) {
          ctx.beginPath();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.3 - wave * 0.08;
          ctx.lineWidth = 2;
          for (let x = 0; x < w; x++) {
            const y = h / 2 + Math.sin((x / w) * Math.PI * (freq / 3) + phase + wave * 0.8) * (h * 0.35) * Math.sin((x / w) * Math.PI);
            x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
      } else {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.15;
        ctx.lineWidth = 1;
        for (let x = 0; x < w; x++) {
          const y = h / 2 + Math.sin((x / w) * Math.PI * 4) * 8;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, freq, brainwaveType]);

  useEffect(() => {
    if (nodesRef.current.masterGain && audioCtxRef.current) {
      nodesRef.current.masterGain.gain.setValueAtTime(volume / 100, audioCtxRef.current.currentTime);
    }
  }, [volume]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const brainwaveColor = { Delta: "brainwave-delta", Theta: "brainwave-theta", Alpha: "brainwave-alpha", Beta: "brainwave-beta" }[brainwaveType] || "brainwave-alpha";
  const brainwaveBg = brainwaveColor + "-bg";

  return (
    <div className={`glass rounded-2xl ${compact ? 'p-4' : 'p-6 sm:p-8'}`} data-testid="sound-player">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${isPlaying ? 'bg-green-400 animate-pulse' : 'bg-muted-foreground/40'}`} />
          <span className="text-sm font-medium text-muted-foreground">
            {isPlaying ? 'Session Active' : 'Ready to Play'}
          </span>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-mono-freq border ${brainwaveBg}`}>
          <span className={brainwaveColor}>{brainwaveType} {freq}Hz</span>
        </div>
      </div>

      {/* Waveform Visualization */}
      <div className="mb-4">
        <canvas ref={canvasRef} className="waveform-canvas" style={{ height: '60px' }} />
      </div>

      {/* Main Display */}
      <div className="text-center mb-8">
        <div className="font-mono-freq text-5xl sm:text-6xl font-bold tracking-tight mb-2" data-testid="timer-display">
          {formatTime(isPlaying ? timeLeft : duration * 60)}
        </div>
        <p className="text-sm text-muted-foreground">
          {condition?.name ? `Sound Therapy for ${condition.name}` : 'Select a condition to begin'}
        </p>
      </div>

      {/* Play Button */}
      <div className="flex justify-center mb-8">
        <button
          onClick={togglePlay}
          data-testid="play-button"
          className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
            isPlaying
              ? 'bg-destructive/20 border-2 border-destructive/50 hover:bg-destructive/30'
              : 'bg-primary/20 border-2 border-primary/50 hover:bg-primary/30'
          } ${isPlaying ? '' : 'player-pulse'}`}
        >
          {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
        </button>
      </div>

      {/* Controls */}
      <div className="space-y-6">
        {/* Volume */}
        <div className="flex items-center gap-4">
          <Volume2 className="w-4 h-4 text-muted-foreground shrink-0" />
          <Slider
            value={[volume]}
            onValueChange={([v]) => setVolume(v)}
            max={100}
            step={1}
            className="flex-1"
            data-testid="volume-slider"
          />
          <span className="text-xs font-mono-freq text-muted-foreground w-10 text-right">{volume}%</span>
        </div>

        {/* Duration */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Duration</span>
          </div>
          <div className="flex gap-2" data-testid="duration-selector">
            {DURATIONS.map(d => (
              <button
                key={d}
                onClick={() => { setDuration(d); if (!isPlaying) setTimeLeft(d * 60); }}
                className={`flex-1 py-2 text-sm rounded-lg transition-all font-mono-freq ${
                  duration === d
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                {d}m
              </button>
            ))}
          </div>
        </div>

        {/* Ambience */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Waves className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Background Ambience</span>
          </div>
          <Select value={ambience} onValueChange={setAmbience} data-testid="ambience-select">
            <SelectTrigger className="bg-muted/50 border-border/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AMBIENCE_TYPES.map(a => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ambience !== "none" && (
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-muted-foreground">Ambience Vol</span>
              <Slider value={[ambienceVolume]} onValueChange={([v]) => setAmbienceVolume(v)} max={100} step={1} className="flex-1" />
              <span className="text-xs font-mono-freq text-muted-foreground w-10 text-right">{ambienceVolume}%</span>
            </div>
          )}
        </div>

        {/* Toggles */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center justify-between flex-1 px-4 py-3 rounded-xl bg-muted/30">
            <span className="text-sm">Solfeggio Frequencies</span>
            <Switch checked={solfeggio} onCheckedChange={setSolfeggio} data-testid="solfeggio-toggle" />
          </div>
          <div className="flex items-center justify-between flex-1 px-4 py-3 rounded-xl bg-muted/30">
            <span className="text-sm">Fade In/Out</span>
            <Switch checked={fadeEffects} onCheckedChange={setFadeEffects} data-testid="fade-toggle" />
          </div>
        </div>

        {/* Headphone notice */}
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent/5 border border-accent/20">
          <Headphones className="w-4 h-4 text-accent shrink-0" />
          <span className="text-xs text-accent/80">
            <a href="https://amzn.to/4rMLOKu" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent">Headphones required</a> for binaural beats to work properly
          </span>
        </div>
      </div>
    </div>
  );
}
